import { graphql } from 'services/api';
import * as queries from './queries';
import * as mutations from './mutations';
import {
  Campaign,
  CampaignSubscriptionWithCampaign,
  CreateCampaignInput,
  GenerateQuestionsResponse,
  InitializeCampaignInput,
  UpdateCampaignInput
} from './types';
import { ModelSortDirection } from 'services/api/types';

export const getCampaign = async ({
  id
}: {
  id: string;
}): Promise<Campaign> => {
  const response = await graphql.query(queries.getCampaign, {
    id
  });

  return response.getCampaign;
};

export const getCampaignsByUserAndOrganization = async ({
  userId,
  organizationId,
  nextToken,
  filter
}: {
  userId: string;
  organizationId: string;
  nextToken: string | null;
  filter?: any;
}): Promise<{ items: Campaign[]; nextToken: string }> => {
  const response = await graphql.query(
    queries.getCampaignsByUserAndOrganization,
    {
      userId,
      organizationId,
      nextToken,
      sortDirection: ModelSortDirection.DESC,
      limit: 400,
      filter
    }
  );

  return response.getCampaignsByUserAndOrganization;
};

export const getCampaignsByOrganization = async ({
  organizationId,
  nextToken,
  filter
}: {
  organizationId: string;
  nextToken: string | null;
  filter?: any;
}): Promise<{ items: Campaign[]; nextToken: string }> => {
  const response = await graphql.query(queries.getCampaignsByOrganization, {
    organizationId,
    nextToken,
    sortDirection: ModelSortDirection.DESC,
    limit: 1000,
    filter
  });

  return response.getCampaignsByOrganization;
};

export const createCampaign = async (
  createCampaignInput: CreateCampaignInput
): Promise<Campaign> => {
  const response = await graphql.mutation(
    mutations.createCampaign,
    createCampaignInput
  );

  return response.createCampaign;
};

export const launchCampaign = async ({
  campaignId,
  launchScheduleDateTime
}: {
  campaignId: string;
  launchScheduleDateTime?: string;
}) => {
  const response = await graphql.mutation(mutations.startSendingMessages, {
    campaignId,
    launchScheduleDateTime
  });

  return response.startSendingMessages;
};

export const startGeneratingMessages = async ({
  campaignId,
  closePreviousConversations
}: {
  campaignId: string;
  closePreviousConversations?: boolean;
}) => {
  const response = await graphql.mutation(mutations.startCampaign, {
    campaignId,
    closePreviousConversations
  });

  return response.startCampaign;
};

export const updateCampaign = async (
  input: UpdateCampaignInput
): Promise<Campaign> => {
  const response = await graphql.mutation(mutations.updateCampaign, input);

  return response.updateCampaign;
};

export const initializeCampaign = async (
  input: InitializeCampaignInput
): Promise<Campaign> => {
  const response = await graphql.mutation(mutations.initializeCampaign, input);

  return response.initializeCampaign;
};

export const createCampaignCsvKeys = async ({
  campaignId,
  csvS3Key,
  organizationId
}: {
  campaignId: string;
  csvS3Key: string;
  organizationId: string;
}): Promise<{
  campaignId: string;
  csvS3Key: string;
  id: string;
  isProcessed: boolean;
}> => {
  const response = await graphql.mutation(mutations.createCampaignCsvKeys, {
    campaignId,
    csvS3Key,
    organizationId
  });

  return response.createCampaignCsvKeys;
};

export const deleteCampaign = async ({
  id
}: {
  id: string;
}): Promise<{ id: string }> => {
  const response = await graphql.mutation(mutations.deleteCampaign, {
    id
  });

  return response.deleteCampaign;
};

export const generateQuestions = async (
  content: string
): Promise<GenerateQuestionsResponse> => {
  const response = await graphql.query(queries.generateQuestions, {
    content
  });

  return response.generateQuestions;
};

export const getCampaignSubscriptionsByUserAndOrganization = async ({
  userId,
  organizationId,
  filter
}: {
  userId: string;
  organizationId: string;
  filter?: any;
}) => {
  // The API answers one page at a time, so follow nextToken to the end. This
  // list decides which shared campaigns a recruiter can see, and stopping at
  // the first page would drop the rest with nothing to show they were missing.
  const items: CampaignSubscriptionWithCampaign[] = [];
  let nextToken: string | null | undefined;

  do {
    const response = await graphql.query(
      queries.getCampaignSubscriptionsByUserAndOrganization,
      {
        userId,
        organizationId,
        filter,
        nextToken
      }
    );

    const page = response.getCampaignSubscriptionsByUserAndOrganization;
    items.push(...(page?.items ?? []));
    nextToken = page?.nextToken;
  } while (nextToken);

  return { items };
};

export const exportCampaignData = async ({
  campaignId
}: {
  campaignId: string;
}): Promise<{ url: string }> => {
  const response = await graphql.query(queries.exportCampaignData, {
    campaignId
  });

  return response.exportCampaignData;
};
